import { useCallback, useEffect, useState } from "react";

import { authFetch } from "@/api/client";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { Pill } from "@/components/Pill";
import { useStore } from "@/state/store";

interface WithdrawalView {
  id: string;
  user_id: string;
  asset: string;
  network: string;
  destination_address: string;
  amount_requested: string;
  network_fee: string;
  amount_to_send: string;
  status: string;
  requested_at: string;
}

interface WithdrawalsResponse {
  items: WithdrawalView[];
  next_cursor?: string | null;
}

type StatusTone = "neutral" | "yes" | "no" | "accent" | "warn";

const STATUS_TONE: Record<string, StatusTone> = {
  requested: "warn",
  pending_review: "warn",
  approved: "yes",
  rejected: "no",
  completed: "accent",
  failed: "no",
};

/**
 * Coda dei prelievi in attesa di revisione (status = `requested` o
 * `pending_review`). Operatori (operation + admin) possono approvare o
 * rifiutare ciascuna richiesta; le azioni sono atomiche lato backend.
 */
export function WithdrawalsPage() {
  const showToast = useStore((s) => s.showToast);

  const [withdrawals, setWithdrawals] = useState<WithdrawalView[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [approving, setApproving] = useState<WithdrawalView | null>(null);
  const [rejecting, setRejecting] = useState<WithdrawalView | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await authFetch("/v1/admin/withdrawals/queue?limit=100");
      if (!res.ok) throw new Error(`withdrawals ${res.status}`);
      const data = (await res.json()) as WithdrawalsResponse;
      setWithdrawals(data.items ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "errore sconosciuto");
      setWithdrawals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns: DataTableColumn<WithdrawalView>[] = [
    {
      key: "id",
      header: "ID",
      cell: (w) => (
        <span
          className="font-mono text-[11px] text-text-2"
          title={w.id}
        >
          {shortId(w.id)}
        </span>
      ),
    },
    {
      key: "user",
      header: "Utente",
      cell: (w) => (
        <span className="font-mono text-[11px] text-muted" title={w.user_id}>
          {shortId(w.user_id)}
        </span>
      ),
      sortValue: (w) => w.user_id,
    },
    {
      key: "asset",
      header: "Asset",
      cell: (w) => (
        <span className="text-[12.5px]">
          <span className="text-text">{w.asset}</span>
          <span className="text-muted"> / {w.network}</span>
        </span>
      ),
      sortValue: (w) => `${w.asset}:${w.network}`,
    },
    {
      key: "destination",
      header: "Destinazione",
      cell: (w) => (
        <span
          className="font-mono text-[11px] text-text-2"
          title={w.destination_address}
        >
          {truncateAddress(w.destination_address)}
        </span>
      ),
    },
    {
      key: "amount",
      header: "Importo",
      align: "right",
      cell: (w) => (
        <div className="text-right">
          <div className="font-mono text-[12.5px] text-text">
            {w.amount_to_send}
          </div>
          <div className="font-mono text-[10.5px] text-muted">
            req {w.amount_requested} − fee {w.network_fee}
          </div>
        </div>
      ),
      sortValue: (w) => parseFloat(w.amount_to_send) || 0,
    },
    {
      key: "status",
      header: "Status",
      cell: (w) => (
        <Pill tone={STATUS_TONE[w.status] ?? "neutral"}>{w.status}</Pill>
      ),
      sortValue: (w) => w.status,
    },
    {
      key: "requested_at",
      header: "Richiesto",
      cell: (w) => (
        <span className="font-mono text-[11.5px] text-muted">
          {formatDateTime(w.requested_at)}
        </span>
      ),
      sortValue: (w) => new Date(w.requested_at),
    },
    {
      key: "actions",
      header: "Azioni",
      align: "right",
      cell: (w) => (
        <div className="inline-flex gap-2 justify-end">
          <button
            type="button"
            className="btn-secondary py-1 px-3 text-[11.5px] border-yes/40 text-yes hover:bg-yes/10"
            onClick={(e) => {
              e.stopPropagation();
              setApproving(w);
            }}
          >
            Approva
          </button>
          <button
            type="button"
            className="btn-secondary py-1 px-3 text-[11.5px] border-no/40 text-no hover:bg-no/10"
            onClick={(e) => {
              e.stopPropagation();
              setRejecting(w);
            }}
          >
            Rifiuta
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4 max-w-[1400px]">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold">Prelievi</h1>
          <p className="text-[13px] text-muted mt-1">
            Coda delle richieste di prelievo in attesa di approvazione manuale.
          </p>
        </div>
        <div className="card py-2 px-3 text-[12px]">
          <span className="text-muted">In coda </span>
          <span className="font-mono text-accent">{withdrawals.length}</span>
        </div>
      </header>

      {err && (
        <div className="card text-[12.5px] text-no">
          Caricamento prelievi fallito: {err}
        </div>
      )}

      {loading ? (
        <div className="card text-muted text-[13px]">Caricamento…</div>
      ) : (
        <DataTable
          rows={withdrawals}
          columns={columns}
          rowKey={(w) => w.id}
          pageSize={25}
          empty={
            <div className="px-4 py-8 text-center text-muted text-[13px]">
              Nessun prelievo in coda.
            </div>
          }
        />
      )}

      {approving && (
        <ApproveModal
          withdrawal={approving}
          onClose={() => setApproving(null)}
          onDone={() => {
            setApproving(null);
            showToast({
              kind: "success",
              message: `Prelievo ${shortId(approving.id)} approvato.`,
            });
            load();
          }}
        />
      )}

      {rejecting && (
        <RejectModal
          withdrawal={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            showToast({
              kind: "success",
              message: `Prelievo ${shortId(rejecting.id)} rifiutato.`,
            });
            load();
          }}
        />
      )}
    </div>
  );
}

function shortId(id: string): string {
  if (!id) return "—";
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function truncateAddress(addr: string): string {
  if (!addr) return "—";
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 8)}…${addr.slice(-6)}`;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Modal di conferma per l'approvazione. La sola conferma esplicita evita
 *  click accidentali — l'approvazione triggera il signing on-chain. */
function ApproveModal({
  withdrawal,
  onClose,
  onDone,
}: {
  withdrawal: WithdrawalView;
  onClose: () => void;
  onDone: () => void;
}) {
  const showToast = useStore((s) => s.showToast);
  const [submitting, setSubmitting] = useState(false);

  async function handleApprove() {
    setSubmitting(true);
    try {
      const res = await authFetch(
        `/v1/admin/withdrawals/${withdrawal.id}/approve`,
        { method: "POST" },
      );
      if (!res.ok) {
        let detail: string | null = null;
        try {
          const body = await res.json();
          detail = body.detail ?? body.message ?? null;
        } catch {
          /* ignore */
        }
        throw new Error(detail ?? `Approvazione fallita (${res.status})`);
      }
      onDone();
    } catch (e) {
      showToast({
        kind: "error",
        message: e instanceof Error ? e.message : "errore",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-[440px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold">Conferma approvazione</h2>
          <button
            type="button"
            className="text-muted hover:text-text text-[18px] leading-none"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        <p className="text-[13px] text-text-2 mb-4">
          Confermi l&apos;approvazione del prelievo? L&apos;operazione
          avvierà il signing on-chain.
        </p>
        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[12px] mb-4 font-mono">
          <dt className="text-muted">ID</dt>
          <dd className="text-text-2 break-all">{withdrawal.id}</dd>
          <dt className="text-muted">Asset</dt>
          <dd className="text-text-2">
            {withdrawal.asset} / {withdrawal.network}
          </dd>
          <dt className="text-muted">Destinazione</dt>
          <dd className="text-text-2 break-all">
            {withdrawal.destination_address}
          </dd>
          <dt className="text-muted">Importo</dt>
          <dd className="text-text">{withdrawal.amount_to_send}</dd>
        </dl>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleApprove}
            disabled={submitting}
          >
            {submitting ? "Invio…" : "Approva"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Modal di rifiuto. Il `reason` è obbligatorio: chi rifiuta deve giustificare
 *  l'azione (il reason finisce nell'audit log). */
function RejectModal({
  withdrawal,
  onClose,
  onDone,
}: {
  withdrawal: WithdrawalView;
  onClose: () => void;
  onDone: () => void;
}) {
  const showToast = useStore((s) => s.showToast);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleReject() {
    if (!reason.trim()) {
      showToast({ kind: "error", message: "Inserisci una motivazione." });
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(
        `/v1/admin/withdrawals/${withdrawal.id}/reject`,
        {
          method: "POST",
          body: JSON.stringify({ reason: reason.trim() }),
        },
      );
      if (!res.ok) {
        let detail: string | null = null;
        try {
          const body = await res.json();
          detail = body.detail ?? body.message ?? null;
        } catch {
          /* ignore */
        }
        throw new Error(detail ?? `Rifiuto fallito (${res.status})`);
      }
      onDone();
    } catch (e) {
      showToast({
        kind: "error",
        message: e instanceof Error ? e.message : "errore",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-[440px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold">Rifiuta prelievo</h2>
          <button
            type="button"
            className="text-muted hover:text-text text-[18px] leading-none"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        <div className="text-[12px] mb-3">
          <div className="text-muted">Prelievo</div>
          <div className="font-mono text-text-2 break-all">{withdrawal.id}</div>
        </div>
        <label className="flex flex-col gap-1 mb-4">
          <span className="text-[11.5px] text-muted">Motivazione</span>
          <textarea
            className="input min-h-[96px] resize-y"
            placeholder="es. KYC insufficiente, indirizzo flagged, ecc."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={handleReject}
            disabled={submitting || !reason.trim()}
          >
            {submitting ? "Invio…" : "Rifiuta"}
          </button>
        </div>
      </div>
    </div>
  );
}
